import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getToken, Config } from '../../services/config';
import ImageCropModal from '../../components/ImageCropModal';
import '../../theme/theme.css';
import './CompanySettings.css';

interface Empresa {
  id: number;
  cnpj: string;
  razao_social: string;
  nome_fantasia: string;
  email: string;
  telefone: string;
  suporte_telefone?: string;
  endereco: any;
  site?: string;
  inscricao_estadual?: string;
  inscricao_municipal?: string;
  responsavel_nome?: string;
  responsavel_cargo?: string;
  responsavel_telefone?: string;
  responsavel_email?: string;
  logomarca_url?: string;
  plano: string;
  ativo: boolean;
  criado_em?: string;
  atualizado_em?: string;
}

interface Estatisticas {
  veiculos: { total: number; ativos: number };
  motoristas: { total: number; ativos: number };
  usuarios: { total: number; ativos: number };
  whatsapp: { total: number; ativas: number };
  equipamentos: { total: number; ativos: number };
  checklists: { total: number; concluidos: number };
  obras: { total: number; em_andamento: number };
}

const CompanySettings: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'dados' | 'estatisticas'>('dados');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [empresa, setEmpresa] = useState<Empresa | null>(null);
  const [estatisticas, setEstatisticas] = useState<Estatisticas | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Empresa>>({});
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [pwaSlug, setPwaSlug] = useState('');
  const [pwaUrl, setPwaUrl] = useState('');
  const [savingSlug, setSavingSlug] = useState(false);
  const [slugCopied, setSlugCopied] = useState(false);

  useEffect(() => {
    fetchEmpresaData();
    fetchEstatisticas();
    fetchPwaSlug();
  }, []);

  useEffect(() => {
    if (empresa) {
      setFormData({
        razao_social: empresa.razao_social,
        nome_fantasia: empresa.nome_fantasia,
        email: empresa.email,
        telefone: empresa.telefone,
        suporte_telefone: empresa.suporte_telefone || '',
        site: empresa.site || '',
        inscricao_estadual: empresa.inscricao_estadual || '',
        inscricao_municipal: empresa.inscricao_municipal || '',
        responsavel_nome: empresa.responsavel_nome || '',
        responsavel_cargo: empresa.responsavel_cargo || '',
        responsavel_telefone: empresa.responsavel_telefone || '',
        responsavel_email: empresa.responsavel_email || '',
        endereco: empresa.endereco || {}
      });
      // Adicionar cache-busting usando timestamp de atualização
      if (empresa.logomarca_url) {
        const timestamp = empresa.atualizado_em ? new Date(empresa.atualizado_em).getTime() : Date.now();
        setLogoPreview(`${empresa.logomarca_url}?v=${timestamp}`);
      } else {
        setLogoPreview(null);
      }
    }
  }, [empresa]);

  const fetchEmpresaData = async () => {
    try {
      const token = getToken();
      console.log('📱 CompanySettings - Dispositivo:', /Android|webOS|iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop');
      console.log('🔑 CompanySettings - Token existe:', !!token);
      console.log('🌐 CompanySettings - API URL:', Config.API_BASE_URL);
      console.log('🚀 CompanySettings - Iniciando requisição...');

      const response = await axios.get(`${Config.API_BASE_URL}/empresas/perfil`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000 // 30 segundos
      });

      console.log('✅ CompanySettings - Resposta recebida:', response.status);
      setEmpresa(response.data.data);
    } catch (error: any) {
      console.error('❌ CompanySettings - Erro detalhado:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        code: error.code
      });

      let errorMessage = 'Erro ao carregar dados da empresa';
      if (error.response) {
        errorMessage = error.response.data?.message || `Erro ${error.response.status}: ${error.response.statusText}`;
      } else if (error.request) {
        errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Tempo de requisição esgotado. Tente novamente.';
      }

      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchEstatisticas = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${Config.API_BASE_URL}/empresas/estatisticas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEstatisticas(response.data.data);
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEnderecoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      endereco: {
        ...(prev.endereco || {}),
        [name]: value
      }
    }));
  };

  const fetchPwaSlug = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${Config.API_BASE_URL}/empresas/pwa-slug`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setPwaSlug(response.data.data.pwa_slug || '');
        setPwaUrl(response.data.data.pwa_url || '');
      }
    } catch (error) {
      console.error('Erro ao carregar slug PWA:', error);
    }
  };

  const handleSavePwaSlug = async () => {
    if (!pwaSlug.trim()) {
      alert('O slug nao pode ser vazio');
      return;
    }
    setSavingSlug(true);
    try {
      const token = getToken();
      const response = await axios.put(
        `${Config.API_BASE_URL}/empresas/pwa-slug`,
        { slug: pwaSlug },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        alert('Link do PWA atualizado com sucesso!');
        fetchPwaSlug();
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao atualizar slug');
    } finally {
      setSavingSlug(false);
    }
  };

  const handleCopyPwaUrl = () => {
    if (pwaUrl) {
      navigator.clipboard.writeText(pwaUrl);
      setSlugCopied(true);
      setTimeout(() => setSlugCopied(false), 2000);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = getToken();
      const response = await axios.put(
        `${Config.API_BASE_URL}/empresas/perfil`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setEmpresa(response.data.data);
      alert('Dados da empresa atualizados com sucesso!');
    } catch (error: any) {
      console.error('Erro ao atualizar dados:', error);
      alert(error.response?.data?.message || 'Erro ao atualizar dados da empresa');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Limpar o input para permitir selecionar o mesmo arquivo novamente
    e.target.value = '';

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione uma imagem válida');
      return;
    }

    // Validar tamanho (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('A imagem deve ter no máximo 5MB');
      return;
    }

    // Abrir modal de crop
    setSelectedImageFile(file);
    setShowCropModal(true);
  };

  const handleCropComplete = async (croppedBlob: Blob, fileName: string) => {
    setShowCropModal(false);
    setUploadingLogo(true);

    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', croppedBlob, fileName);

      const response = await axios.post(
        `${Config.API_BASE_URL}/empresas/logomarca`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setLogoPreview(response.data.data.logomarca_url);
      alert('Logomarca atualizada com sucesso!');
      await fetchEmpresaData();
    } catch (error: any) {
      console.error('Erro ao fazer upload da logomarca:', error);
      alert(error.response?.data?.message || 'Erro ao fazer upload da logomarca');
    } finally {
      setUploadingLogo(false);
      setSelectedImageFile(null);
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setSelectedImageFile(null);
  };

  const handleUseOriginal = async (file: File) => {
    setShowCropModal(false);
    setUploadingLogo(true);

    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${Config.API_BASE_URL}/empresas/logomarca`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setLogoPreview(response.data.data.logomarca_url);
      alert('Logomarca atualizada com sucesso!');
      await fetchEmpresaData();
    } catch (error: any) {
      console.error('Erro ao fazer upload da logomarca:', error);
      alert(error.response?.data?.message || 'Erro ao fazer upload da logomarca');
    } finally {
      setUploadingLogo(false);
      setSelectedImageFile(null);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Deseja realmente remover a logomarca?')) return;

    try {
      const token = getToken();
      await axios.delete(`${Config.API_BASE_URL}/empresas/logomarca`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setLogoPreview(null);
      alert('Logomarca removida com sucesso!');
      fetchEmpresaData();
    } catch (error: any) {
      console.error('Erro ao remover logomarca:', error);
      alert(error.response?.data?.message || 'Erro ao remover logomarca');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Carregando configurações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/configuracoes')}>
          <i className="fas fa-arrow-left"></i>
        </button>
        <div>
          <h1>Configurações da Empresa</h1>
          <p className="page-subtitle">
            Gerencie as informações cadastrais e visualize estatísticas da empresa
          </p>
        </div>
      </div>

      <div className="company-settings-tabs">
        <button
          className={`tab-btn ${activeTab === 'dados' ? 'active' : ''}`}
          onClick={() => setActiveTab('dados')}
        >
          <i className="fas fa-building"></i>
          Dados Cadastrais
        </button>
        <button
          className={`tab-btn ${activeTab === 'estatisticas' ? 'active' : ''}`}
          onClick={() => setActiveTab('estatisticas')}
        >
          <i className="fas fa-chart-bar"></i>
          Estatísticas
        </button>
      </div>

      <div className="page-content">
        {activeTab === 'dados' && (
          <div className="company-data-section">
            {/* Seção de Logomarca */}
            <div className="company-logo-section card">
              <h3>
                <i className="fas fa-image"></i>
                Logomarca da Empresa
              </h3>
              <div className="logo-upload-container">
                <div className="logo-preview">
                  {logoPreview ? (
                    <div className="logo-preview-with-actions">
                      <img src={logoPreview} alt="Logomarca" crossOrigin="anonymous" />
                      <button
                        className="btn-delete-overlay"
                        onClick={handleDeleteLogo}
                        disabled={uploadingLogo}
                        title="Remover logomarca"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  ) : (
                    <div className="logo-placeholder">
                      <i className="fas fa-building"></i>
                      <p>Nenhuma logomarca cadastrada</p>
                    </div>
                  )}
                </div>
                <div className="logo-actions">
                  <label
                    className={`btn ${logoPreview ? 'btn-secondary' : 'btn-primary'}`}
                    htmlFor="logo-upload"
                  >
                    <i className={`fas ${uploadingLogo ? 'fa-spinner fa-spin' : logoPreview ? 'fa-sync-alt' : 'fa-upload'}`}></i>
                    {uploadingLogo ? 'Enviando...' : logoPreview ? 'Trocar Logomarca' : 'Enviar Logomarca'}
                  </label>
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoSelect}
                    disabled={uploadingLogo}
                    style={{ display: 'none' }}
                  />
                  <p className="help-text">
                    <i className="fas fa-info-circle"></i>
                    Formatos aceitos: JPG, PNG, GIF. Tamanho máximo: 5MB.
                    {logoPreview ? ' Clique em "Trocar Logomarca" para substituir.' : ' Você poderá ajustar a imagem antes de enviar.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Formulário de Dados */}
            <form onSubmit={handleSubmit} className="company-form">
              {/* Informações Básicas */}
              <div className="card">
                <h3>
                  <i className="fas fa-info-circle"></i>
                  Informações Básicas
                </h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>CNPJ</label>
                    <input
                      type="text"
                      value={empresa?.cnpj || ''}
                      disabled
                      className="form-control disabled"
                    />
                    <small className="help-text">CNPJ não pode ser alterado</small>
                  </div>

                  <div className="form-group">
                    <label>Plano</label>
                    <input
                      type="text"
                      value={empresa?.plano || ''}
                      disabled
                      className="form-control disabled"
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Razão Social *</label>
                    <input
                      type="text"
                      name="razao_social"
                      value={formData.razao_social || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      required
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Nome Fantasia</label>
                    <input
                      type="text"
                      name="nome_fantasia"
                      value={formData.nome_fantasia || ''}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Telefone</label>
                    <input
                      type="text"
                      name="telefone"
                      value={formData.telefone || ''}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label>Telefone de Suporte</label>
                    <input
                      type="text"
                      name="suporte_telefone"
                      value={formData.suporte_telefone || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="(00) 00000-0000"
                    />
                    <small className="help-text">Exibido nas etiquetas de QR Code de veículos e equipamentos</small>
                  </div>

                  <div className="form-group">
                    <label>Website</label>
                    <input
                      type="url"
                      name="site"
                      value={formData.site || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="https://www.exemplo.com.br"
                    />
                  </div>

                  <div className="form-group">
                    <label>Inscrição Estadual</label>
                    <input
                      type="text"
                      name="inscricao_estadual"
                      value={formData.inscricao_estadual || ''}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label>Inscrição Municipal</label>
                    <input
                      type="text"
                      name="inscricao_municipal"
                      value={formData.inscricao_municipal || ''}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>
                </div>
              </div>

              {/* Endereço */}
              <div className="card">
                <h3>
                  <i className="fas fa-map-marker-alt"></i>
                  Endereço
                </h3>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Logradouro</label>
                    <input
                      type="text"
                      name="logradouro"
                      value={formData.endereco?.logradouro || ''}
                      onChange={handleEnderecoChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label>Número</label>
                    <input
                      type="text"
                      name="numero"
                      value={formData.endereco?.numero || ''}
                      onChange={handleEnderecoChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label>Complemento</label>
                    <input
                      type="text"
                      name="complemento"
                      value={formData.endereco?.complemento || ''}
                      onChange={handleEnderecoChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label>Bairro</label>
                    <input
                      type="text"
                      name="bairro"
                      value={formData.endereco?.bairro || ''}
                      onChange={handleEnderecoChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label>Cidade</label>
                    <input
                      type="text"
                      name="cidade"
                      value={formData.endereco?.cidade || ''}
                      onChange={handleEnderecoChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label>Estado</label>
                    <input
                      type="text"
                      name="estado"
                      value={formData.endereco?.estado || ''}
                      onChange={handleEnderecoChange}
                      className="form-control"
                      maxLength={2}
                    />
                  </div>

                  <div className="form-group">
                    <label>CEP</label>
                    <input
                      type="text"
                      name="cep"
                      value={formData.endereco?.cep || ''}
                      onChange={handleEnderecoChange}
                      className="form-control"
                    />
                  </div>
                </div>
              </div>

              {/* Acesso PWA do Motorista */}
              <div className="card">
                <h3>
                  <i className="fas fa-mobile-alt"></i>
                  Acesso PWA do Motorista
                </h3>
                <p style={{ color: '#7f8c8d', fontSize: '0.9rem', marginBottom: '16px' }}>
                  Configure o link de acesso do motorista ao aplicativo PWA. Compartilhe este link com seus motoristas.
                </p>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Slug de Acesso</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        value={pwaSlug}
                        onChange={(e) => setPwaSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                        className="form-control"
                        placeholder="ex: minha-transportadora"
                        maxLength={50}
                      />
                      <button
                        type="button"
                        onClick={handleSavePwaSlug}
                        disabled={savingSlug}
                        style={{
                          padding: '8px 16px', background: '#04C64F', color: 'white',
                          border: 'none', borderRadius: '6px', cursor: 'pointer',
                          whiteSpace: 'nowrap', fontSize: '0.85rem'
                        }}
                      >
                        {savingSlug ? 'Salvando...' : 'Salvar'}
                      </button>
                    </div>
                    <small className="help-text">Apenas letras minusculas, numeros e hifen. Padrao: CNPJ da empresa.</small>
                  </div>

                  <div className="form-group">
                    <label>Link do PWA</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        value={pwaUrl}
                        readOnly
                        className="form-control disabled"
                        style={{ background: '#f8f9fa' }}
                      />
                      <button
                        type="button"
                        onClick={handleCopyPwaUrl}
                        disabled={!pwaUrl}
                        style={{
                          padding: '8px 16px', background: slugCopied ? '#27ae60' : '#3498db', color: 'white',
                          border: 'none', borderRadius: '6px', cursor: 'pointer',
                          whiteSpace: 'nowrap', fontSize: '0.85rem'
                        }}
                      >
                        <i className={`fas ${slugCopied ? 'fa-check' : 'fa-copy'}`}></i> {slugCopied ? 'Copiado!' : 'Copiar'}
                      </button>
                    </div>
                    <small className="help-text">Compartilhe este link com seus motoristas para acesso ao PWA.</small>
                  </div>
                </div>
              </div>

              {/* Responsável */}
              <div className="card">
                <h3>
                  <i className="fas fa-user-tie"></i>
                  Responsável / Contato Principal
                </h3>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Nome do Responsável</label>
                    <input
                      type="text"
                      name="responsavel_nome"
                      value={formData.responsavel_nome || ''}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label>Cargo</label>
                    <input
                      type="text"
                      name="responsavel_cargo"
                      value={formData.responsavel_cargo || ''}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label>Telefone</label>
                    <input
                      type="text"
                      name="responsavel_telefone"
                      value={formData.responsavel_telefone || ''}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Email</label>
                    <input
                      type="email"
                      name="responsavel_email"
                      value={formData.responsavel_email || ''}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>
                </div>
              </div>

              {/* Botões de Ação */}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate('/configuracoes')}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={saving}
                >
                  <i className="fas fa-save"></i>
                  {saving ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'estatisticas' && estatisticas && (
          <div className="statistics-section">
            <div className="stats-grid">
              {/* Veículos */}
              <div className="stat-card card">
                <div className="stat-icon vehicles">
                  <i className="fas fa-truck"></i>
                </div>
                <div className="stat-content">
                  <h3>Veículos</h3>
                  <div className="stat-numbers">
                    <div className="stat-item">
                      <span className="stat-label">Total:</span>
                      <span className="stat-value">{estatisticas.veiculos.total}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Ativos:</span>
                      <span className="stat-value success">{estatisticas.veiculos.ativos}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Motoristas */}
              <div className="stat-card card">
                <div className="stat-icon drivers">
                  <i className="fas fa-id-card"></i>
                </div>
                <div className="stat-content">
                  <h3>Motoristas</h3>
                  <div className="stat-numbers">
                    <div className="stat-item">
                      <span className="stat-label">Total:</span>
                      <span className="stat-value">{estatisticas.motoristas.total}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Ativos:</span>
                      <span className="stat-value success">{estatisticas.motoristas.ativos}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Usuários */}
              <div className="stat-card card">
                <div className="stat-icon users">
                  <i className="fas fa-users"></i>
                </div>
                <div className="stat-content">
                  <h3>Usuários</h3>
                  <div className="stat-numbers">
                    <div className="stat-item">
                      <span className="stat-label">Total:</span>
                      <span className="stat-value">{estatisticas.usuarios.total}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Ativos:</span>
                      <span className="stat-value success">{estatisticas.usuarios.ativos}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* WhatsApp */}
              <div className="stat-card card">
                <div className="stat-icon whatsapp">
                  <i className="fab fa-whatsapp"></i>
                </div>
                <div className="stat-content">
                  <h3>Conexões WhatsApp</h3>
                  <div className="stat-numbers">
                    <div className="stat-item">
                      <span className="stat-label">Total:</span>
                      <span className="stat-value">{estatisticas.whatsapp.total}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Conectadas:</span>
                      <span className="stat-value success">{estatisticas.whatsapp.ativas}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Equipamentos */}
              <div className="stat-card card">
                <div className="stat-icon equipment">
                  <i className="fas fa-tools"></i>
                </div>
                <div className="stat-content">
                  <h3>Equipamentos</h3>
                  <div className="stat-numbers">
                    <div className="stat-item">
                      <span className="stat-label">Total:</span>
                      <span className="stat-value">{estatisticas.equipamentos.total}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Ativos:</span>
                      <span className="stat-value success">{estatisticas.equipamentos.ativos}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Checklists */}
              <div className="stat-card card">
                <div className="stat-icon checklists">
                  <i className="fas fa-clipboard-check"></i>
                </div>
                <div className="stat-content">
                  <h3>Checklists</h3>
                  <div className="stat-numbers">
                    <div className="stat-item">
                      <span className="stat-label">Total:</span>
                      <span className="stat-value">{estatisticas.checklists.total}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Concluídos:</span>
                      <span className="stat-value success">{estatisticas.checklists.concluidos}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Obras */}
              <div className="stat-card card">
                <div className="stat-icon projects">
                  <i className="fas fa-hard-hat"></i>
                </div>
                <div className="stat-content">
                  <h3>Obras</h3>
                  <div className="stat-numbers">
                    <div className="stat-item">
                      <span className="stat-label">Total:</span>
                      <span className="stat-value">{estatisticas.obras.total}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Em Andamento:</span>
                      <span className="stat-value success">{estatisticas.obras.em_andamento}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="stats-refresh">
              <button
                className="btn btn-primary"
                onClick={fetchEstatisticas}
              >
                <i className="fas fa-sync-alt"></i>
                Atualizar Estatísticas
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Crop de Imagem */}
      {showCropModal && selectedImageFile && (
        <ImageCropModal
          imageFile={selectedImageFile}
          onCropComplete={handleCropComplete}
          onUseOriginal={handleUseOriginal}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
};

export default CompanySettings;
